import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import {
  addCombatant,
  emptyBattleState,
  nextTurn,
  previousTurn,
  reorderInitiative,
} from '../domain/battle';
import { applyExhaustionHpCap, applyHpDelta, setTempHp } from '../domain/damage';
import { exhaustionLevel, isConditionImmune } from '../domain/conditions';
import type {
  ActionResolutionSession,
  BattleState,
  Combatant,
  ConditionInstance,
  EntityId,
} from '../domain/types';

export interface BattleSessionState {
  battle: BattleState;
  selectedTargetUids: EntityId[];
  selectedActionId: EntityId | null;
  resolution: ActionResolutionSession | null;
  pendingExhaustionDeath: { targetUid: EntityId; targetName: string } | null;
}

const initialState: BattleSessionState = {
  battle: emptyBattleState(),
  selectedTargetUids: [],
  selectedActionId: null,
  resolution: null,
  pendingExhaustionDeath: null,
};

export const battleSlice = createSlice({
  name: 'battleSession',
  initialState,
  reducers: {
    hydrateBattle: (_state, action: PayloadAction<BattleSessionState>) => action.payload,
    resetBattle: () => initialState,
    addParticipant: (state, action: PayloadAction<Combatant>) => {
      state.battle = addCombatant(state.battle, action.payload);
    },
    removeParticipant: (state, action: PayloadAction<EntityId>) => {
      state.battle.participants = state.battle.participants.filter((p) => p.uid !== action.payload);
      state.battle.currentIndex = Math.min(
        state.battle.currentIndex,
        Math.max(0, state.battle.participants.length - 1),
      );
      state.selectedTargetUids = state.selectedTargetUids.filter((uid) => uid !== action.payload);
      if (state.pendingExhaustionDeath?.targetUid === action.payload) {
        state.pendingExhaustionDeath = null;
      }
    },
    updateParticipant: (
      state,
      action: PayloadAction<{
        uid: EntityId;
        changes: Partial<Omit<Combatant, 'uid'>>;
      }>,
    ) => {
      const participant = state.battle.participants.find((item) => item.uid === action.payload.uid);
      if (participant) Object.assign(participant, action.payload.changes);
    },
    replaceParticipants: (state, action: PayloadAction<Combatant[]>) => {
      const currentUid = state.battle.participants[state.battle.currentIndex]?.uid;
      state.battle.participants = action.payload;
      state.battle.currentIndex = currentUid
        ? Math.max(
            0,
            state.battle.participants.findIndex((participant) => participant.uid === currentUid),
          )
        : 0;
    },
    setCurrentActor: (state, action: PayloadAction<EntityId>) => {
      const index = state.battle.participants.findIndex((p) => p.uid === action.payload);
      if (index >= 0) state.battle.currentIndex = index;
    },
    advanceTurn: (state) => {
      state.battle = nextTurn(state.battle);
      state.selectedTargetUids = [];
      state.selectedActionId = null;
    },
    rewindTurn: (state) => {
      state.battle = previousTurn(state.battle);
      state.selectedTargetUids = [];
    },
    reorderParticipants: (state, action: PayloadAction<{ from: number; to: number }>) => {
      state.battle = reorderInitiative(state.battle, action.payload.from, action.payload.to);
    },
    toggleTarget: (state, action: PayloadAction<EntityId>) => {
      const index = state.selectedTargetUids.indexOf(action.payload);
      if (index >= 0) state.selectedTargetUids.splice(index, 1);
      else state.selectedTargetUids.push(action.payload);
    },
    selectTargets: (state, action: PayloadAction<EntityId[]>) => {
      state.selectedTargetUids = action.payload;
    },
    selectAction: (state, action: PayloadAction<EntityId | null>) => {
      state.selectedActionId = action.payload;
    },
    setActionCooldown: (
      state,
      action: PayloadAction<{ uid: EntityId; actionId: EntityId; cooldown: number }>,
    ) => {
      const participant = state.battle.participants.find((item) => item.uid === action.payload.uid);
      const combatAction = participant?.actions.find((item) => item.id === action.payload.actionId);
      if (combatAction) combatAction.cooldown = Math.max(0, action.payload.cooldown);
    },
    changeHp: (state, action: PayloadAction<{ uid: EntityId; delta: number }>) => {
      const index = state.battle.participants.findIndex((p) => p.uid === action.payload.uid);
      const participant = state.battle.participants[index];
      if (participant)
        state.battle.participants[index] = applyHpDelta(participant, action.payload.delta);
    },
    changeTempHp: (state, action: PayloadAction<{ uid: EntityId; amount: number }>) => {
      const index = state.battle.participants.findIndex((p) => p.uid === action.payload.uid);
      const participant = state.battle.participants[index];
      if (participant)
        state.battle.participants[index] = setTempHp(participant, action.payload.amount);
    },
    addCondition: (state, action: PayloadAction<{ uid: EntityId; status: ConditionInstance }>) => {
      const participant = state.battle.participants.find((p) => p.uid === action.payload.uid);
      if (!participant || isConditionImmune(participant, action.payload.status.key)) return;
      if (participant.statuses.some((status) => status.id === action.payload.status.id)) return;
      const previousExhaustion = exhaustionLevel(participant);
      if (action.payload.status.key === 'exhaustion') {
        const incoming = action.payload.status;
        const level = Math.min(6, Math.max(1, Math.floor(incoming.level ?? 1)));
        const rounds = Math.max(1, Math.floor(incoming.rounds));
        const stepRounds = Math.max(1, Math.floor(incoming.stepRounds ?? rounds));
        const existing = participant.statuses.find((status) => status.key === 'exhaustion');
        if (existing) {
          existing.level = Math.max(existing.level ?? 1, level);
          existing.rounds = Math.max(existing.rounds, rounds);
          existing.stepRounds = Math.max(existing.stepRounds ?? existing.rounds, stepRounds);
        } else {
          participant.statuses.push({ ...incoming, level, rounds, stepRounds });
        }
      } else {
        participant.statuses.push(action.payload.status);
      }
      const index = state.battle.participants.indexOf(participant);
      state.battle.participants[index] = applyExhaustionHpCap(
        participant,
        exhaustionLevel(participant),
      );
      const nextExhaustion = exhaustionLevel(participant);
      if (previousExhaustion < 6 && nextExhaustion >= 6) {
        state.pendingExhaustionDeath = {
          targetUid: participant.uid,
          targetName: participant.name,
        };
      }
    },
    removeCondition: (state, action: PayloadAction<{ uid: EntityId; statusId: EntityId }>) => {
      const participant = state.battle.participants.find((p) => p.uid === action.payload.uid);
      if (!participant) return;
      participant.statuses = participant.statuses.filter((s) => s.id !== action.payload.statusId);
      const index = state.battle.participants.indexOf(participant);
      state.battle.participants[index] = applyExhaustionHpCap(
        participant,
        exhaustionLevel(participant),
      );
    },
    startResolution: (state, action: PayloadAction<ActionResolutionSession>) => {
      state.resolution = action.payload;
    },
    updateResolution: (state, action: PayloadAction<ActionResolutionSession>) => {
      state.resolution = action.payload;
    },
    clearResolution: (state) => {
      state.resolution = null;
    },
    resolveExhaustionDeath: (state, action: PayloadAction<boolean>) => {
      const pending = state.pendingExhaustionDeath;
      state.pendingExhaustionDeath = null;
      if (!pending) return;
      const participant = state.battle.participants.find((item) => item.uid === pending.targetUid);
      if (!participant) return;
      if (action.payload) {
        participant.hpCurrent = 0;
        return;
      }
      const exhaustion = participant.statuses.find((status) => status.key === 'exhaustion');
      if (exhaustion) exhaustion.level = 5;
      const index = state.battle.participants.indexOf(participant);
      state.battle.participants[index] = applyExhaustionHpCap(participant, 5);
    },
  },
});

export const battleActions = battleSlice.actions;
